import React from 'react';
import { Button } from './ui/button';
import { useNavigate } from 'react-router-dom';
import { Gamepad2, ArrowRight, Download, Star } from 'lucide-react';
import CorporateHeader from './CorporateHeader';
import Footer from './Footer';

const ThisOrThatLandingPage = () => {
  const navigate = useNavigate();

  const volumes = [
    {
      title: 'Vol. 1 — How Well Do You Know Them?',
      description: 'The original conversation-starter. Perfect for friends, family, and game nights.',
      image: 'https://customer-assets.emergentagent.com/job_audrey-portfolio/artifacts/dezhjave_1%20THIS%20or%20THAT%20How%20Well%20Do%20You%20Know%20Them%20Vol%201.%20cover.png',
      price: '$9.99',
      available: true,
      packageId: 'this_or_that_vol1',
    },
    {
      title: 'Vol. 2 — How Well Do You Know Your Partner?',
      description: 'Spark deeper conversations with your significant other. The couples edition.',
      image: 'https://customer-assets.emergentagent.com/job_audrey-portfolio/artifacts/rw6b567o_2%20THIS%20or%20THAT%20How%20Well%20Do%20You%20Know%20Your%20Partner%20Vol%202%20-%20cover.png',
      price: '$9.99',
      available: true,
      packageId: 'this_or_that_vol2',
    },
    {
      title: 'Vol. 3',
      description: 'Continue the conversation with even more engaging questions.',
      image: 'https://customer-assets.emergentagent.com/job_egypt-portfolio/artifacts/o60phml6_THIS%20or%20THAT%20Vol.%203.png',
      available: false,
    },
    {
      title: 'Vol. 4 — GameBook Series',
      description: 'A new spin on game night with the GameBook edition.',
      image: 'https://customer-assets.emergentagent.com/job_egypt-portfolio/artifacts/eohhys62_THIS%20or%20THAT%20Vol.%204%20-%20GameBook%20Series.png',
      available: false,
    },
    {
      title: 'Vol. 5 — Guess All 10 Slang Twins',
      description: 'The slang edition — how well do you really know the lingo?',
      image: 'https://customer-assets.emergentagent.com/job_egypt-portfolio/artifacts/fd9cg33h_THIS%20or%20THAT%20Vol.%205%20-%20GUESS%20ALL%2010%20SLANG%20TWINS%20Vol.%205.png',
      available: false,
    },
    {
      title: 'Kids Edition — Ages 6-12',
      description: 'Fun, age-appropriate questions designed for young minds.',
      image: 'https://customer-assets.emergentagent.com/job_4ba17392-007a-438c-b296-76e166f0f0cf/artifacts/scvtvbsw_THIS%20OR%20THATThe%20Choice%20is%20yYours%20-%20Ages%20%286-12%29.png',
      available: false,
    },
  ];

  const handlePurchase = async (packageId) => {
    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL || '';
      const res = await fetch(`${API_URL}/api/create-checkout-session`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ package_id: packageId, origin_url: window.location.origin })
      });
      const data = await res.json();
      if (data.url) window.location.href = data.url;
    } catch (err) {
      alert('Unable to process payment. Please try again.');
    }
  };

  return (
    <div className="min-h-screen bg-black">
      <CorporateHeader />

      {/* Hero */}
      <section className="pt-28 pb-20 relative overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-red-900/30 via-transparent to-transparent" />
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 items-center max-w-7xl mx-auto">
            <div>
              <div className="inline-flex items-center gap-2 text-red-400 text-sm font-semibold mb-4">
                <Gamepad2 className="w-4 h-4" />
                BOOK SERIES
              </div>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black text-white mb-4" data-testid="thisorthat-title">
                THIS or <span className="text-red-500">THAT</span>
              </h1>
              <p className="text-xl text-gray-400 leading-relaxed mb-6">
                The ultimate conversation-starting book series. Multiple volumes for every occasion — couples, families, kids, and game night. Which will you choose?
              </p>
              <div className="flex items-center gap-4 mb-8">
                <div className="flex text-yellow-400">
                  {[...Array(5)].map((_, i) => <Star key={i} className="w-5 h-5 fill-current" />)}
                </div>
                <span className="text-gray-400 text-sm">Bestselling Series</span>
              </div>
              <Button onClick={() => document.getElementById('volumes').scrollIntoView({ behavior: 'smooth' })} className="bg-red-600 hover:bg-red-700 text-white font-bold px-8 py-3 text-lg">
                Browse All Volumes <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </div>
            <div className="flex justify-center">
              <img
                src="https://customer-assets.emergentagent.com/job_4ba17392-007a-438c-b296-76e166f0f0cf/artifacts/fssq7muy_A%20new%20spin%20on%20GameNight%20-%20THIS%20or%20THAT.png"
                alt="THIS or THAT Series"
                className="w-full max-w-lg rounded-xl shadow-2xl shadow-red-900/30"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Marketing Image */}
      <section className="py-12 bg-gradient-to-b from-black to-gray-900">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <img
            src="https://customer-assets.emergentagent.com/job_audrey-portfolio/artifacts/envuhssr_Marketing%20materials%20for%20THIS%20or%20THAT%20book%20series.png"
            alt="THIS or THAT Marketing"
            className="max-w-4xl w-full mx-auto rounded-xl shadow-xl"
          />
        </div>
      </section>

      {/* Volumes Grid */}
      <section id="volumes" className="py-16 bg-black">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-white mb-8 text-center">All Volumes</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto">
            {volumes.map((vol) => (
              <div key={vol.title} className={`bg-gray-900/50 border border-gray-800 rounded-2xl overflow-hidden transition-all duration-300 hover:border-red-600/40 ${!vol.available ? 'opacity-70' : ''}`}>
                <div className="aspect-square">
                  <img src={vol.image} alt={vol.title} className="w-full h-full object-cover" />
                </div>
                <div className="p-6">
                  <h3 className="text-lg font-bold text-white mb-2">{vol.title}</h3>
                  <p className="text-gray-400 text-sm mb-4">{vol.description}</p>
                  <div className="flex items-center justify-between">
                    {vol.price && <span className="text-red-400 font-bold text-xl">{vol.price}</span>}
                    {vol.available ? (
                      <Button onClick={() => handlePurchase(vol.packageId)} size="sm" className="bg-red-600 hover:bg-red-700 text-white" data-testid={`buy-${vol.packageId}`}>
                        <Download className="w-4 h-4 mr-1" />
                        Buy Now
                      </Button>
                    ) : (
                      <Button disabled size="sm" className="bg-gray-700 text-gray-400 cursor-not-allowed">Coming Soon</Button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-gradient-to-b from-black to-gray-900">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold text-white mb-4">Visit the Full Store</h2>
          <p className="text-gray-400 mb-8 max-w-xl mx-auto">Browse all eBlackBookListings products including digital assets, soundtracks, and bundles.</p>
          <Button onClick={() => navigate('/store')} className="bg-red-600 hover:bg-red-700 text-white font-bold px-8 py-4 text-lg">
            Visit Store <ArrowRight className="w-5 h-5 ml-2" />
          </Button>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ThisOrThatLandingPage;
